import { getSceneModelId } from '../Renders/viewerStateUtils';
import {
    buildClearAlignerThumbnailItems,
    buildThumbnailTypeUrl,
    CLEAR_ALIGNER_TEMPLATE_NAME,
} from '../thumbnailHelpers';

const scanLabelAliases = {
    upper_arch: 'upper',
    lower_arch: 'lower',
};

function normalizeScanLabelValue(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function getCanonicalScanLabel(scan) {
    const normalizedLabel = normalizeScanLabelValue(scan.label || scan.displayLabel);

    return scanLabelAliases[normalizedLabel] || normalizedLabel;
}

function getMediaScanDisplayName(scan, fallbackLabel) {
    const canonicalLabel = getCanonicalScanLabel(scan);

    if (canonicalLabel === 'buccal_left') {
        return 'Bite (left)';
    }

    if (canonicalLabel === 'buccal_right') {
        return 'Bite (right)';
    }

    return scan.displayLabel || scan.label || fallbackLabel;
}

function buildLegacyThumbnailTypes(scan) {
    const canonicalLabel = getCanonicalScanLabel(scan);
    const rawLabel = normalizeScanLabelValue(scan.label);
    const rawDisplayLabel = normalizeScanLabelValue(scan.displayLabel);
    const typeSet = new Set([
        rawLabel,
        rawDisplayLabel,
        canonicalLabel,
    ].filter(Boolean));

    if (canonicalLabel.includes('upper')) {
        typeSet.add('upper');
    }

    if (canonicalLabel.includes('lower')) {
        typeSet.add('lower');
    }

    if (canonicalLabel.includes('buccal') || canonicalLabel.includes('bite')) {
        typeSet.add('bite');
        typeSet.add('buccal_left');
        typeSet.add('buccal_right');
    }

    return Array.from(typeSet);
}

function buildThumbnailUrlCandidates(scan, caseThumbnailUrl) {
    const candidates = [];
    const legacyTypes = buildLegacyThumbnailTypes(scan);

    if (scan.thumbnailUrl) {
        candidates.push(scan.thumbnailUrl);

        legacyTypes.forEach((type) => {
            candidates.push(buildThumbnailTypeUrl(scan.thumbnailUrl, type));
        });
    }

    if (caseThumbnailUrl) {
        legacyTypes.forEach((type) => {
            candidates.push(buildThumbnailTypeUrl(caseThumbnailUrl, type));
        });
    }

    return Array.from(new Set(candidates.filter(Boolean)));
}

function buildScanMediaScans(scans, caseThumbnailUrl) {
    return scans
        .map((scan, index) => ({
            canonicalLabel: getCanonicalScanLabel(scan),
            id: getSceneModelId(scan, index),
            label: getMediaScanDisplayName(scan, `Scan ${index + 1}`),
            originalIndex: index,
            thumbnailUrlCandidates: buildThumbnailUrlCandidates(scan, caseThumbnailUrl),
            typeLabel: 'Thumbnail',
        }))
        .filter((scan) => scan.thumbnailUrlCandidates.length > 0)
        .sort((leftScan, rightScan) => {
            if (leftScan.canonicalLabel === 'buccal_left' && rightScan.canonicalLabel === 'buccal_right') {
                return -1;
            }

            if (leftScan.canonicalLabel === 'buccal_right' && rightScan.canonicalLabel === 'buccal_left') {
                return 1;
            }

            return leftScan.originalIndex - rightScan.originalIndex;
        })
        .map(({ canonicalLabel, originalIndex, ...scan }) => scan);
}

export function buildMediaScans(scans, caseThumbnailUrl, template) {
    const scanMediaScans = buildScanMediaScans(scans, caseThumbnailUrl);

    if (template !== CLEAR_ALIGNER_TEMPLATE_NAME) {
        return scanMediaScans;
    }

    return [
        ...scanMediaScans,
        ...buildClearAlignerThumbnailItems(caseThumbnailUrl),
    ];
}
